import { useEffect, useState } from 'react';
import { MapPin, ShieldAlert, Loader2, Wifi, WifiOff } from 'lucide-react';

/**
 * Persistent GPS health pill — shown while a worker is clocked in.
 *  - Green = recent ping (<2 min)
 *  - Amber = stale (2–5 min)
 *  - Red   = permission denied / 5+ min gap / unsupported
 * Tap to retry getCurrentPosition + open help drawer if denied.
 */
export function GpsTrackerBadge({ totalKm, status, error, lastPingTs }) {
  const [tick, setTick] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  // Re-render every 15s so the "X min ago" updates
  useEffect(() => {
    if (status === 'idle') return;
    const t = setInterval(() => setTick(x => x + 1), 15_000);
    return () => clearInterval(t);
  }, [status]);

  if (status === 'idle') return null;

  // Compute staleness in seconds
  let ageSec = null;
  if (lastPingTs) {
    ageSec = Math.floor((Date.now() - new Date(lastPingTs).getTime()) / 1000);
  }

  let tone, Icon, label;
  if (status === 'denied' || status === 'unsupported' || status === 'error') {
    tone = 'bg-rose-600/95 text-white ring-2 ring-rose-400 animate-pulse';
    Icon = ShieldAlert;
    label = status === 'denied' ? 'LOCATION OFF — tap to fix'
          : status === 'unsupported' ? 'GPS not supported'
          : (error || 'GPS error');
  } else if (status === 'requesting') {
    tone = 'bg-slate-700/90 text-white';
    Icon = Loader2;
    label = 'Acquiring GPS…';
  } else if (ageSec != null && ageSec > 300) {
    tone = 'bg-rose-600/95 text-white ring-2 ring-rose-400';
    Icon = WifiOff;
    label = `No ping ${Math.floor(ageSec / 60)}m — tap to retry`;
  } else if (ageSec != null && ageSec > 120) {
    tone = 'bg-amber-500/95 text-white';
    Icon = Wifi;
    label = `Last ping ${Math.floor(ageSec / 60)}m ago · ${(totalKm || 0).toFixed(2)} km`;
  } else {
    tone = 'bg-emerald-600/95 text-white';
    Icon = MapPin;
    label = `${(totalKm || 0).toFixed(2)} km tracked`;
  }

  const handleClick = () => {
    if (status === 'denied' || status === 'unsupported') {
      setHelpOpen(o => !o);
      return;
    }
    // Try to nudge the browser into providing a fresh fix
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        () => { /* triggers the existing watcher to also rerun */ },
        () => setHelpOpen(true),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`fixed bottom-24 right-4 z-40 px-3 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium transition-transform hover:scale-105 ${tone}`}
        data-testid="gps-tracker-badge"
        role="status"
        aria-live="polite"
        title={ageSec != null ? `Last GPS: ${ageSec}s ago` : ''}
      >
        <Icon className={`w-4 h-4 ${status === 'requesting' ? 'animate-spin' : ''}`} />
        <span data-testid="gps-tracker-km">{label}</span>
        <span className="sr-only">tick={tick}</span>
      </button>

      {helpOpen && (
        <div className="fixed bottom-40 right-4 z-50 max-w-xs bg-white text-slate-800 rounded-lg shadow-2xl p-4 border border-slate-200" data-testid="gps-help-drawer">
          <p className="font-semibold text-sm mb-2 text-rose-700">Location is OFF — travel KMs aren't being tracked.</p>
          <ul className="text-xs space-y-1.5 mb-2">
            <li><strong>iPhone:</strong> Settings → Safari → Location → Allow. Refresh.</li>
            <li><strong>Android:</strong> Tap 🔒 next to the URL → Permissions → Location → Allow.</li>
            <li><strong>Desktop:</strong> Click 🔒 → Site settings → Location → Allow.</li>
          </ul>
          <button
            onClick={() => setHelpOpen(false)}
            className="text-xs text-teal-700 hover:text-teal-900 font-medium"
          >
            Got it
          </button>
        </div>
      )}
    </>
  );
}
